/**
 * Comprehensive logout service to handle logout across the entire application
 * Ensures proper cleanup of all session data, authentication tokens, and user state
 */

import sessionService from './sessionService.js';
import dataLoadingService from './dataLoadingService.js';

class LogoutService {
  constructor() {
    this.logoutListeners = new Set();
    this.isLoggingOut = false;
  }

  /**
   * Register a component to listen for logout events
   */
  addLogoutListener(callback) {
    console.log('🔐 Adding logout listener');
    this.logoutListeners.add(callback);
    
    return () => {
      console.log('🔐 Removing logout listener');
      this.logoutListeners.delete(callback);
    };
  }

  /**
   * Notify all listeners that logout is happening
   */
  notifyLogoutListeners() {
    console.log('🔐 Notifying logout listeners');
    this.logoutListeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in logout listener:', error);
      }
    });
  }

  /**
   * Comprehensive logout function
   */
  async logout(options = {}) {
    const {
      skipRedirect = false,
      redirectUrl = '/login',
      showLoading = true,
      reason = 'user_initiated'
    } = options;

    // Prevent multiple simultaneous logout attempts
    if (this.isLoggingOut) {
      console.log('🔐 Logout already in progress, skipping');
      return;
    }

    this.isLoggingOut = true;
    console.log('🔐 Starting comprehensive logout process...', { reason });

    try {
      // Step 1: Show loading state if requested
      if (showLoading) {
        this.showLogoutLoading();
      }

      // Step 2: Notify all components that logout is starting
      this.notifyLogoutListeners();

      // Step 3: Clear backend session
      try {
        console.log('🔐 Clearing backend session...');
        await sessionService.logout();
        console.log('✅ Backend session cleared');
      } catch (error) {
        console.warn('⚠️ Failed to clear backend session:', error);
        // Continue with logout even if backend fails
      }

      // Step 4: Clear all localStorage data
      this.clearLocalStorage();

      // Step 5: Clear all sessionStorage data
      this.clearSessionStorage();

      // Step 6: Reset data loading service
      dataLoadingService.reset();

      // Step 7: Clear any cached API data
      this.clearApiCaches();

      // Step 8: Clear any WebSocket connections
      this.clearWebSocketConnections();

      // Step 9: Clear any timers or intervals
      this.clearTimersAndIntervals();

      // Step 10: Dispatch logout event for any remaining listeners
      this.dispatchLogoutEvent();

      console.log('✅ Logout process completed successfully');

      // Step 11: Redirect to login page
      if (!skipRedirect) {
        this.redirectToLogin(redirectUrl);
      }

    } catch (error) {
      console.error('❌ Error during logout process:', error);
      // Even if there's an error, still try to redirect
      if (!skipRedirect) {
        this.redirectToLogin(redirectUrl);
      }
    } finally {
      this.isLoggingOut = false;
      this.hideLogoutLoading();
    }
  }

  /**
   * Clear all localStorage data
   */
  clearLocalStorage() {
    console.log('🔐 Clearing localStorage...');
    
    const keysToKeep = [
      'theme', // Keep user theme preference
      'language', // Keep language preference
      'ui-preferences' // Keep UI preferences
    ];

    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        try {
          localStorage.removeItem(key);
          console.log(`🗑️ Removed localStorage key: ${key}`);
        } catch (error) {
          console.warn(`Failed to remove localStorage key ${key}:`, error);
        }
      }
    });

    console.log('✅ localStorage cleared');
  }

  /**
   * Clear all sessionStorage data
   */
  clearSessionStorage() {
    console.log('🔐 Clearing sessionStorage...');
    
    try {
      sessionStorage.clear();
      console.log('✅ sessionStorage cleared');
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error);
    }
  }

  /**
   * Clear API caches
   */
  clearApiCaches() {
    console.log('🔐 Clearing API caches...');
    
    // Clear any cached API responses
    if (window.apiCache) {
      window.apiCache.clear();
    }

    // Clear any service worker caches related to API
    if ('serviceWorker' in navigator && 'caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          if (cacheName.includes('api') || cacheName.includes('auth')) {
            caches.delete(cacheName);
            console.log(`🗑️ Cleared cache: ${cacheName}`);
          }
        });
      });
    }

    console.log('✅ API caches cleared');
  }

  /**
   * Clear WebSocket connections
   */
  clearWebSocketConnections() {
    console.log('🔐 Clearing WebSocket connections...');
    
    // Close any global WebSocket connections
    if (window.websocketService) {
      try {
        window.websocketService.disconnect();
        console.log('✅ WebSocket service disconnected');
      } catch (error) {
        console.warn('Failed to disconnect WebSocket service:', error);
      }
    }

    // Close any other WebSocket connections
    if (window.webSocketConnections) {
      window.webSocketConnections.forEach(ws => {
        try {
          ws.close();
        } catch (error) {
          console.warn('Failed to close WebSocket connection:', error);
        }
      });
      window.webSocketConnections = [];
    }

    console.log('✅ WebSocket connections cleared');
  }

  /**
   * Clear timers and intervals
   */
  clearTimersAndIntervals() {
    console.log('🔐 Clearing timers and intervals...');
    
    // Clear any global timers
    if (window.appTimers) {
      window.appTimers.forEach(timerId => {
        clearTimeout(timerId);
        clearInterval(timerId);
      });
      window.appTimers = [];
    }

    console.log('✅ Timers and intervals cleared');
  }

  /**
   * Dispatch logout event
   */
  dispatchLogoutEvent() {
    console.log('🔐 Dispatching logout event...');
    
    const logoutEvent = new CustomEvent('userLogout', {
      detail: { timestamp: Date.now() }
    });
    
    window.dispatchEvent(logoutEvent);
    console.log('✅ Logout event dispatched');
  }

  /**
   * Show logout loading state
   */
  showLogoutLoading() {
    // Create a simple loading overlay
    const overlay = document.createElement('div');
    overlay.id = 'logout-loading-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      color: white;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 40px; height: 40px; border: 4px solid #ffffff30; border-top: 4px solid white; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <div>Signing out...</div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.appendChild(overlay);
  }

  /**
   * Hide logout loading state
   */
  hideLogoutLoading() {
    const overlay = document.getElementById('logout-loading-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Redirect to login page
   */
  redirectToLogin(redirectUrl = '/login') {
    console.log(`🔐 Redirecting to: ${redirectUrl}`);
    
    // Use window.location.href for guaranteed redirect
    window.location.href = redirectUrl;
  }

  /**
   * Emergency logout - for when normal logout fails
   */
  emergencyLogout() {
    console.log('🚨 Emergency logout initiated');
    
    // Clear everything we can
    try {
      localStorage.clear();
    } catch (e) {}
    
    try {
      sessionStorage.clear();
    } catch (e) {}
    
    // Force redirect
    window.location.href = '/login';
  }

  /**
   * Check if currently logging out
   */
  isLoggingOutNow() {
    return this.isLoggingOut;
  }
}

// Create singleton instance
const logoutService = new LogoutService();

export default logoutService;
